/**
 * PagesContext — React context for managing the Pages module state:
 * libraries, page tree, active page, revisions, and search results.
 *
 * Follows the ActiveBoardContext pattern with useReducer + useCallback.
 */

import React, { createContext, useCallback, useContext, useReducer, useRef } from 'react';
import * as pagesApi from '../api/pages-api';
import type { Library, LibraryVisibility } from '../api/pages-api';
import { midpoint } from '../utils/fractional-index';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** Minimal page node for the tree sidebar */
export interface PageTreeNode {
  readonly pageId: string;
  readonly title: string;
  readonly parentPageId: string | null;
  readonly sortOrder: string;
  readonly children: PageTreeNode[];
}

/** Full page detail including content */
export interface PageDetail {
  readonly pageId: string;
  readonly title: string;
  readonly libraryId: string;
  readonly parentPageId: string | null;
  readonly authorId: string;
  readonly status: string;
  readonly version: number;
  readonly content: unknown; // BlockNote JSON
  readonly createdAt: string;
  readonly updatedAt: string;
}

/** Revision summary for the history panel */
export interface RevisionSummary {
  readonly timestamp: string;
  readonly authorId: string;
  readonly changeNote?: string;
}

/** Search result item */
export interface PageSearchResult {
  readonly pageId: string;
  readonly title: string;
  readonly libraryId: string;
  readonly snippet?: string;
}

/** Options for creating a new page */
export interface CreatePageOpts {
  readonly title: string;
  readonly parentPageId?: string | null;
  readonly content?: unknown;
  readonly templateId?: string;
}

// ---------------------------------------------------------------------------
// State
// ---------------------------------------------------------------------------

export interface PagesState {
  readonly libraries: Library[];
  readonly selectedLibraryId: string | null;
  readonly pageTree: PageTreeNode[];
  readonly activePage: PageDetail | null;
  readonly pageAccessLevel: string | null;
  readonly revisions: RevisionSummary[];
  readonly searchResults: PageSearchResult[];
  readonly loading: boolean;
  readonly error: string | null;
  readonly hasUnsavedChanges: boolean;
}

export const initialPagesState: PagesState = {
  libraries: [],
  selectedLibraryId: null,
  pageTree: [],
  activePage: null,
  pageAccessLevel: null,
  revisions: [],
  searchResults: [],
  loading: false,
  error: null,
  hasUnsavedChanges: false,
};

// ---------------------------------------------------------------------------
// Actions
// ---------------------------------------------------------------------------

export type PagesAction =
  | { type: 'FETCH_LIBRARIES_START' }
  | { type: 'FETCH_LIBRARIES_SUCCESS'; payload: Library[] }
  | { type: 'FETCH_LIBRARIES_ERROR'; payload: string }
  | { type: 'SELECT_LIBRARY'; payload: string | null }
  | { type: 'CLEAR_ACTIVE_PAGE' }
  | { type: 'FETCH_PAGE_TREE_START' }
  | { type: 'FETCH_PAGE_TREE_SUCCESS'; payload: { libraryId: string; tree: PageTreeNode[] } }
  | { type: 'FETCH_PAGE_TREE_ERROR'; payload: string }
  | { type: 'FETCH_PAGE_START' }
  | { type: 'FETCH_PAGE_SUCCESS'; payload: { page: PageDetail; accessLevel: string | null } }
  | { type: 'FETCH_PAGE_ERROR'; payload: string }
  | { type: 'SAVE_PAGE_START' }
  | { type: 'SAVE_PAGE_SUCCESS'; payload: PageDetail }
  | { type: 'SAVE_PAGE_ERROR'; payload: string }
  | { type: 'CREATE_PAGE_SUCCESS'; payload: { pageId: string; node: PageTreeNode } }
  | { type: 'DELETE_PAGE_SUCCESS'; payload: { pageId: string } }
  | { type: 'MOVE_PAGE_OPTIMISTIC'; payload: { pageId: string; newParentId: string | null } }
  | { type: 'MOVE_PAGE_REVERT'; payload: { previousTree: PageTreeNode[] } }
  | { type: 'SET_SEARCH_RESULTS'; payload: PageSearchResult[] }
  | { type: 'SET_HAS_UNSAVED_CHANGES'; payload: boolean }
  | { type: 'CLEAR_ERROR' }
  // Real-time left-nav events (WebSocket nav channel). All inserts are
  // idempotent so echoed/duplicate/reconnect-replay events are safe no-ops.
  | { type: 'WS_LIBRARY_CREATED'; payload: { library: Library } }
  | { type: 'WS_LIBRARY_UPDATED'; payload: { libraryId: string; name?: string; visibility?: LibraryVisibility } }
  | { type: 'WS_LIBRARY_DELETED'; payload: { libraryId: string } }
  | { type: 'WS_PAGE_CREATED'; payload: { libraryId: string; node: PageTreeNode } }
  | { type: 'WS_PAGE_UPDATED'; payload: { libraryId: string; pageId: string; title: string } }
  | { type: 'WS_PAGE_MOVED'; payload: { libraryId: string; pageId: string; parentPageId: string | null; sortOrder: string } }
  | { type: 'WS_PAGE_DELETED'; payload: { libraryId: string; pageId: string } };

// ---------------------------------------------------------------------------
// Reducer
// ---------------------------------------------------------------------------

export function pagesReducer(state: PagesState, action: PagesAction): PagesState {
  switch (action.type) {
    case 'FETCH_LIBRARIES_START':
      return { ...state, loading: true, error: null };

    case 'FETCH_LIBRARIES_SUCCESS':
      return { ...state, loading: false, libraries: action.payload };

    case 'FETCH_LIBRARIES_ERROR':
      return { ...state, loading: false, error: action.payload };

    case 'SELECT_LIBRARY':
      return { ...state, selectedLibraryId: action.payload, pageTree: [], activePage: null };

    case 'CLEAR_ACTIVE_PAGE':
      return { ...state, activePage: null, pageAccessLevel: null };

    case 'FETCH_PAGE_TREE_START':
      return { ...state, loading: true, error: null };

    case 'FETCH_PAGE_TREE_SUCCESS':
      // Only apply tree data if it belongs to the currently selected library.
      // This prevents a race condition where a slow response from a previous
      // library overwrites the tree after the user has already switched.
      if (action.payload.libraryId !== state.selectedLibraryId) {
        return state;
      }
      return { ...state, loading: false, pageTree: action.payload.tree };

    case 'FETCH_PAGE_TREE_ERROR':
      return { ...state, loading: false, error: action.payload };

    case 'FETCH_PAGE_START':
      return { ...state, loading: true, error: null };

    case 'FETCH_PAGE_SUCCESS':
      return { ...state, loading: false, activePage: action.payload.page, pageAccessLevel: action.payload.accessLevel, hasUnsavedChanges: false };

    case 'FETCH_PAGE_ERROR':
      return { ...state, loading: false, error: action.payload };

    case 'SAVE_PAGE_START':
      return { ...state, loading: true, error: null };

    case 'SAVE_PAGE_SUCCESS':
      return { ...state, loading: false, activePage: action.payload, hasUnsavedChanges: false };

    case 'SAVE_PAGE_ERROR':
      return { ...state, loading: false, error: action.payload };

    case 'CREATE_PAGE_SUCCESS':
      return { ...state, pageTree: insertNodeIntoTree(state.pageTree, action.payload.node) };

    case 'DELETE_PAGE_SUCCESS':
      return {
        ...state,
        pageTree: removeNodeFromTree(state.pageTree, action.payload.pageId),
        activePage: state.activePage?.pageId === action.payload.pageId ? null : state.activePage,
      };

    case 'MOVE_PAGE_OPTIMISTIC':
      return {
        ...state,
        pageTree: moveNodeInTree(state.pageTree, action.payload.pageId, action.payload.newParentId),
      };

    case 'MOVE_PAGE_REVERT':
      return { ...state, pageTree: action.payload.previousTree };

    case 'SET_SEARCH_RESULTS':
      return { ...state, searchResults: action.payload };

    case 'SET_HAS_UNSAVED_CHANGES':
      return { ...state, hasUnsavedChanges: action.payload };

    case 'CLEAR_ERROR':
      return { ...state, error: null };

    // ─── Real-time nav events ────────────────────────────────────────────
    case 'WS_LIBRARY_CREATED': {
      const { library } = action.payload;
      // Idempotent: ignore if a library with this id is already present.
      if (state.libraries.some((l) => l.libraryId === library.libraryId)) {
        return state;
      }
      return { ...state, libraries: [...state.libraries, library] };
    }

    case 'WS_LIBRARY_UPDATED': {
      const { libraryId, name, visibility } = action.payload;
      return {
        ...state,
        libraries: state.libraries.map((l) =>
          l.libraryId === libraryId
            ? {
                ...l,
                ...(name !== undefined ? { name } : {}),
                ...(visibility !== undefined ? { visibility } : {}),
              }
            : l,
        ),
      };
    }

    case 'WS_LIBRARY_DELETED': {
      const { libraryId } = action.payload;
      const wasSelected = state.selectedLibraryId === libraryId;
      return {
        ...state,
        libraries: state.libraries.filter((l) => l.libraryId !== libraryId),
        selectedLibraryId: wasSelected ? null : state.selectedLibraryId,
        pageTree: wasSelected ? [] : state.pageTree,
        activePage: wasSelected ? null : state.activePage,
      };
    }

    case 'WS_PAGE_CREATED': {
      const { libraryId, node } = action.payload;
      // Only the currently-held tree is mutated; other libraries are correct on
      // the next fetch-on-expand.
      if (libraryId !== state.selectedLibraryId) return state;
      // Idempotent: a duplicate/echoed create is a true no-op.
      if (findPageInTree(state.pageTree, node.pageId)) return state;
      // insertNodeIntoTree is a no-op when parentPageId is absent from the held
      // tree (accepted fetch-on-expand behavior); root-level pages insert.
      return { ...state, pageTree: insertNodeIntoTree(state.pageTree, node) };
    }

    case 'WS_PAGE_UPDATED': {
      const { libraryId, pageId, title } = action.payload;
      if (libraryId !== state.selectedLibraryId) return state;
      return { ...state, pageTree: patchNodeInTree(state.pageTree, pageId, { title }) };
    }

    case 'WS_PAGE_MOVED': {
      const { libraryId, pageId, parentPageId } = action.payload;
      if (libraryId !== state.selectedLibraryId) return state;
      // Canonical sort order is reconciled by a follow-up fetchPageTree, as
      // reorderPage already does; here we just reparent.
      return { ...state, pageTree: moveNodeInTree(state.pageTree, pageId, parentPageId) };
    }

    case 'WS_PAGE_DELETED': {
      const { libraryId, pageId } = action.payload;
      if (libraryId !== state.selectedLibraryId) return state;
      return { ...state, pageTree: removeNodeFromTree(state.pageTree, pageId) };
    }

    default:
      return state;
  }
}

// ---------------------------------------------------------------------------
// Tree utility helpers
// ---------------------------------------------------------------------------

function insertNodeIntoTree(tree: PageTreeNode[], node: PageTreeNode): PageTreeNode[] {
  if (!node.parentPageId) {
    return [...tree, node];
  }
  return tree.map((n) => {
    if (n.pageId === node.parentPageId) {
      return { ...n, children: [...n.children, node] };
    }
    if (n.children.length > 0) {
      return { ...n, children: insertNodeIntoTree(n.children, node) };
    }
    return n;
  });
}

function removeNodeFromTree(tree: PageTreeNode[], pageId: string): PageTreeNode[] {
  return tree
    .filter((n) => n.pageId !== pageId)
    .map((n) => ({
      ...n,
      children: removeNodeFromTree(n.children, pageId),
    }));
}

function moveNodeInTree(tree: PageTreeNode[], pageId: string, newParentId: string | null): PageTreeNode[] {
  // Find and extract the node
  let movedNode: PageTreeNode | null = null;

  function extract(nodes: PageTreeNode[]): PageTreeNode[] {
    return nodes
      .filter((n) => {
        if (n.pageId === pageId) {
          movedNode = n;
          return false;
        }
        return true;
      })
      .map((n) => ({ ...n, children: extract(n.children) }));
  }

  const treeWithoutNode = extract(tree);
  if (!movedNode) return tree;

  const updatedNode: PageTreeNode = { ...(movedNode as PageTreeNode), parentPageId: newParentId };
  return insertNodeIntoTree(treeWithoutNode, updatedNode);
}

/**
 * Returns a new tree with the matching node shallow-patched. Recursive and
 * immutable, mirroring removeNodeFromTree. Used by WS_PAGE_UPDATED (title).
 */
function patchNodeInTree(
  tree: PageTreeNode[],
  pageId: string,
  patch: Partial<Pick<PageTreeNode, 'title'>>,
): PageTreeNode[] {
  return tree.map((n) => {
    if (n.pageId === pageId) {
      return { ...n, ...patch, children: patchNodeInTree(n.children, pageId, patch) };
    }
    return { ...n, children: patchNodeInTree(n.children, pageId, patch) };
  });
}

function findPageInTree(tree: PageTreeNode[], pageId: string): PageTreeNode | null {
  for (const node of tree) {
    if (node.pageId === pageId) return node;
    const found = findPageInTree(node.children, pageId);
    if (found) return found;
  }
  return null;
}

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

export interface PagesContextValue {
  readonly state: PagesState;
  fetchLibraries: () => Promise<void>;
  selectLibrary: (libraryId: string) => void;
  clearActivePage: () => void;
  fetchPageTree: (libraryId: string) => Promise<void>;
  fetchPage: (pageId: string) => Promise<void>;
  savePage: (pageId: string, content: unknown) => Promise<void>;
  createPage: (libraryId: string, opts: CreatePageOpts) => Promise<string>;
  deletePage: (pageId: string) => Promise<void>;
  archivePage: (pageId: string) => Promise<'archived' | 'deleted'>;
  movePage: (pageId: string, newParentId: string | null) => Promise<void>;
  reorderPage: (pageId: string, newParentId: string | null, beforeSort: string | null, afterSort: string | null) => Promise<void>;
  setHasUnsavedChanges: (value: boolean) => void;
  dispatch: React.Dispatch<PagesAction>;
}

const PagesContext = createContext<PagesContextValue | null>(null);

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

interface PagesProviderProps {
  readonly children: React.ReactNode;
}

export function PagesProvider({ children }: PagesProviderProps): React.ReactElement {
  const [state, dispatch] = useReducer(pagesReducer, initialPagesState);
  const stateRef = useRef<PagesState>(state);
  stateRef.current = state;

  const fetchLibraries = useCallback(async (): Promise<void> => {
    dispatch({ type: 'FETCH_LIBRARIES_START' });
    try {
      const response = await pagesApi.listLibraries();
      const libraries = response.data ?? (response as any).libraries ?? [];
      dispatch({ type: 'FETCH_LIBRARIES_SUCCESS', payload: libraries });
    } catch (error: unknown) {
      dispatch({ type: 'FETCH_LIBRARIES_ERROR', payload: extractErrorMessage(error) });
    }
  }, []);

  const selectLibrary = useCallback((libraryId: string): void => {
    dispatch({ type: 'SELECT_LIBRARY', payload: libraryId });
  }, []);

  const clearActivePage = useCallback((): void => {
    dispatch({ type: 'CLEAR_ACTIVE_PAGE' });
  }, []);

  const fetchPageTree = useCallback(async (libraryId: string): Promise<void> => {
    dispatch({ type: 'FETCH_PAGE_TREE_START' });
    try {
      const response = await pagesApi.listPages(libraryId);
      const pages = response.data ?? (response as any).pages ?? [];
      dispatch({ type: 'FETCH_PAGE_TREE_SUCCESS', payload: { libraryId, tree: pages } });
    } catch (error: unknown) {
      dispatch({ type: 'FETCH_PAGE_TREE_ERROR', payload: extractErrorMessage(error) });
    }
  }, []);

  const fetchPage = useCallback(async (pageId: string): Promise<void> => {
    dispatch({ type: 'FETCH_PAGE_START' });
    try {
      const response = await pagesApi.getPage(pageId);
      // BFF returns { page: {...}, content: {...}, accessLevel: '...' }
      const pageDetail: PageDetail = (response as any).page
        ? { ...(response as any).page, content: (response as any).content }
        : response as unknown as PageDetail;
      const accessLevel = (response as any).accessLevel as string | null ?? null;
      dispatch({ type: 'FETCH_PAGE_SUCCESS', payload: { page: pageDetail, accessLevel } });
    } catch (error: unknown) {
      dispatch({ type: 'FETCH_PAGE_ERROR', payload: extractErrorMessage(error) });
    }
  }, []);

  const savePage = useCallback(async (pageId: string, content: unknown): Promise<void> => {
    dispatch({ type: 'SAVE_PAGE_START' });
    try {
      const response = await pagesApi.updatePage(pageId, { content });
      dispatch({ type: 'SAVE_PAGE_SUCCESS', payload: response });
    } catch (error: unknown) {
      dispatch({ type: 'SAVE_PAGE_ERROR', payload: extractErrorMessage(error) });
    }
  }, []);

  const createPage = useCallback(async (libraryId: string, opts: CreatePageOpts): Promise<string> => {
    const response = await pagesApi.createPage(libraryId, opts);
    const newNode: PageTreeNode = {
      pageId: response.pageId,
      title: opts.title,
      parentPageId: opts.parentPageId ?? null,
      sortOrder: response.sortOrder ?? 'z0',
      children: [],
    };
    dispatch({ type: 'CREATE_PAGE_SUCCESS', payload: { pageId: response.pageId, node: newNode } });
    return response.pageId;
  }, []);

  const deletePage = useCallback(async (pageId: string): Promise<void> => {
    await pagesApi.deletePage(pageId);
    dispatch({ type: 'DELETE_PAGE_SUCCESS', payload: { pageId } });
  }, []);

  const archivePage = useCallback(async (pageId: string): Promise<'archived' | 'deleted'> => {
    const result = await pagesApi.archivePage(pageId);
    // Both archive and delete remove the page from the tree
    dispatch({ type: 'DELETE_PAGE_SUCCESS', payload: { pageId } });
    return result.action;
  }, []);

  const movePage = useCallback(async (pageId: string, newParentId: string | null): Promise<void> => {
    const previousTree = stateRef.current.pageTree;
    dispatch({ type: 'MOVE_PAGE_OPTIMISTIC', payload: { pageId, newParentId } });
    try {
      await pagesApi.movePage(pageId, { newParentId });
    } catch {
      dispatch({ type: 'MOVE_PAGE_REVERT', payload: { previousTree } });
    }
  }, []);

  const reorderPage = useCallback(async (
    pageId: string,
    newParentId: string | null,
    beforeSort: string | null,
    afterSort: string | null,
  ): Promise<void> => {
    const previousTree = stateRef.current.pageTree;

    // Compute new sort order between siblings
    const newSortOrder = midpoint(beforeSort, afterSort);

    // Optimistic: move the page to new parent position
    dispatch({ type: 'MOVE_PAGE_OPTIMISTIC', payload: { pageId, newParentId } });

    try {
      // If parent is changing, move first
      const currentPage = findPageInTree(previousTree, pageId);
      if (currentPage && currentPage.parentPageId !== newParentId) {
        await pagesApi.movePage(pageId, { newParentId });
      }
      // Then set sort order
      await pagesApi.reorderPage(pageId, { newSortOrder });
      // Refresh tree to get server-canonical order
      const libraryId = stateRef.current.selectedLibraryId;
      if (libraryId) {
        const response = await pagesApi.listPages(libraryId);
        const pages = response.data ?? (response as any).pages ?? [];
        dispatch({ type: 'FETCH_PAGE_TREE_SUCCESS', payload: { libraryId, tree: pages } });
      }
    } catch {
      dispatch({ type: 'MOVE_PAGE_REVERT', payload: { previousTree } });
    }
  }, []);

  const setHasUnsavedChanges = useCallback((value: boolean): void => {
    dispatch({ type: 'SET_HAS_UNSAVED_CHANGES', payload: value });
  }, []);

  const value: PagesContextValue = {
    state,
    fetchLibraries,
    selectLibrary,
    clearActivePage,
    fetchPageTree,
    fetchPage,
    savePage,
    createPage,
    deletePage,
    archivePage,
    movePage,
    reorderPage,
    setHasUnsavedChanges,
    dispatch,
  };

  return <PagesContext.Provider value={value}>{children}</PagesContext.Provider>;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * Access the pages context. Must be used within a PagesProvider.
 */
export function usePagesContext(): PagesContextValue {
  const context = useContext(PagesContext);
  if (!context) {
    throw new Error('usePagesContext must be used within a PagesProvider');
  }
  return context;
}

// ---------------------------------------------------------------------------
// Utilities
// ---------------------------------------------------------------------------

function extractErrorMessage(error: unknown): string {
  if (error && typeof error === 'object' && 'message' in error) {
    return (error as { message: string }).message;
  }
  if (error instanceof Error) {
    return error.message;
  }
  return 'An unexpected error occurred';
}
